import { formatSsn } from './format.util';

export function isAddressPoBoxBase(value: string) {
  return /\b((?:(?:p(?:\.|ost|ostal)?\s*o(?:\.|ffice)?\s*b(?:ox)?))|(?:(?:post|postal)\s+office\s+(?:#|box(?:#|\s+#|\.)?\s*\d))|(?:\b(box(?:#|\s+#|\.)?\s*\d|(?:post|postal|office)\s+box|box(?=\s*\d)|box\.\s*(?=\s*\d)|(?:p\.?|post|postal)\s*o\.?\s*b|p\s*box|PO\s*B\.?|P\.?\s*O\.?\s*B|PO)\b))\b/gim.test(
    value,
  );
}

// helper for checking PO boxes in format #xxxxxx
export function isAddressNumeric(value: string) {
  return /#[0-9]/.test(value);
}

export function isAddressApo(value: string) {
  return /[AFD]PO\sA[AEP]\s\d\d\d\d\d/gim.test(value);
}

export function isAddressPoBox(value: string) {
  return (
    value !== '' &&
    (isAddressPoBoxBase(value) ||
      isAddressNumeric(value) ||
      isAddressApo(value))
  );
}

export function isValidEmail(value: string) {
  return /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(
    value,
  );
}

export function isValidPhoneNumber(value: string) {
  return (
    /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(value) &&
    value.replace(/\D/g, '').length === 10
  );
}

export function isValidUrl(value: string, allowQueryParams = true) {
  if (allowQueryParams) {
    return /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\*\+,;=.]+$/.test(
      value,
    );
  } else {
    return /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/#[\]@!\$&'\*\+,;.]+$/.test(
      value,
    );
  }
}

export function isWithinRange(value: number, min: number, max: number) {
  return value >= min && value <= max;
}

// credit https://gist.github.com/DiegoSalazar/4075533
export function isValidLuhnCreditCard(value = '') {
  // accept only digits, dashes or spaces
  if (/[^0-9-\s]+/.test(value)) return false;

  // The Luhn Algorithm. It's so pretty.
  let sum = 0;
  let oddDigit = 0;
  let isEven = false;
  value = value.replace(/\D/g, '');

  for (let n = value.length - 1; n >= 0; n--) {
    const cDigit = value.charAt(n);
    oddDigit = parseInt(cDigit, 10);

    if (isEven) {
      if ((oddDigit *= 2) > 9) oddDigit -= 9;
    }

    sum += oddDigit;
    isEven = !isEven;
  }
  return sum % 10 === 0;
}

export function isValidCreditCard(value = '') {
  let valid = false;
  const number = String(value).replace(/[\D]/g, '');

  if (!number) {
    // nothing
    valid = false;
  } else if (/^4[0-9]{6,}$/.test(number)) {
    // VISA
    // try to gather one extra digit at end of chunk
    // if it only gets 4 numbers, it's valid
    valid = number.substring(12, 16 + 1).length === 4;
  } else if (/^5[1-5][0-9]{5,}$/.test(number)) {
    // MasterCard
    valid = number.substring(12, 16 + 1).length === 4;
  } else if (/^2[2-7][2][0-9]{3,}$/.test(number)) {
    // MasterCard new bins
    valid = number.substring(12, 16 + 1).length === 4;
  } else if (/^6(?:011|5[0-9]{2})[0-9]{3,}$/.test(number)) {
    // Discover
    valid = number.substring(12, 16 + 1).length === 4;
  } else if (/^3[47][0-9]{5,}$/.test(number)) {
    // AMEX
    valid = number.substring(10, 15 + 1).length === 5;
  } else if (/^3(?:0[0-5]|[68][0-9])[0-9]{11}$/.test(number)) {
    // Diner's Club
    valid = number.substring(10, 14 + 1).length === 4;
  } else if (/^(?:2131|1800\d{3})\d{11}$/.test(number)) {
    // JCB beginning with 2131 or 1800 (15 digits)
    valid = number.substring(10, 15 + 1).length === 5;
  } else if (/^(?:35\d{3})\d{11}$/.test(number)) {
    // JCB beginning with 35 (16 digits)
    valid = number.substring(12, 16 + 1).length === 4;
  }
  return valid && isValidLuhnCreditCard(value);
}

export function isValidSSN(value = '', country?: string) {
  try {
    if (!value || typeof value !== 'string') {
      return false;
    }

    if (country !== 'USA') {
      return value.includes('-')
        ? /^\d\d\d-\d\d-\d\d\d\d$/.test(value)
        : /^\d{9}$/.test(value);
    }

    // see the original ticket: https://fattmerchant.atlassian.net/browse/ROK-402
    // https://www.ssa.gov/employer/randomization.html
    // https://stackoverflow.com/questions/2313704/is-there-a-social-security-number-reserved-for-testing-examples
    const excludedSsn = [
      '457-55-5462',
      '078-05-1120',
      '219-09-9999',
      '123-45-6789',
      '012-34-5678',
      '111-11-1111',
      '222-22-2222',
      '333-33-3333',
      '444-44-4444',
      '555-55-5555',
      '666-66-6666',
      '777-77-7777',
      '888-88-8888',
      '999-99-9999',
    ];

    if (excludedSsn.includes(formatSsn(value))) return false;

    const re = /^(?!000|666)[0-8][0-9]{2}-(?!00)[0-9]{2}-(?!0000)[0-9]{4}$/;
    value = formatSsn(value);
    return re.test(value);
  } catch (e) {
    return false;
  }
}

/** Check if a user input string is meaningful, i.e. string less than 2 characters long or strings with repeat characters like 'aaaaa' are not meaningful */
export function isMeaningfulString(str: string | null): boolean {
  if (str == null || str.length < 2) return false;
  let letterDistribution: { [char: string]: number } =
    Array.prototype.reduce.call(
      str,
      (map: { [char: string]: number }, ch: string) => {
        map[ch] = (map[ch] || 0) + 1;
        return map;
      },
      {},
    );
  // letter frequency, string of entirely one character isn't valid
  if (Object.keys(letterDistribution).length < 2) return false;
  return true;
}
